import axios from 'axios';

// Einfaches Request-Caching um doppelte GET-Requests zu vermeiden
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 1500; // 1.5 Sekunden

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const api = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor um die baseURL dynamisch zu setzen
api.interceptors.request.use((config) => {
  // Prüfe ob lokal auf Port 3020 (Frontend direkt) zugegriffen wird
  const isLocalDev = window.location.port === '3020';
  
  if (isLocalDev) {
    // Lokal: Direkt zum Backend-Port
    const hostname = window.location.hostname;
    config.baseURL = `http://${hostname}:3021/api`;
  } else {
    // Produktion: nginx routet /api an Backend weiter
    config.baseURL = '/api';
  }
  
  return config;
});

// Request Interceptor: Token hinzufügen
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Nur GET-Requests aus Cache prüfen
    if (config.method?.toLowerCase() === 'get') {
      const cacheKey = `${config.baseURL}${config.url}`;
      const cached = requestCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        config.adapter = () => Promise.resolve(cached.data);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Auto-Refresh bei 401
api.interceptors.response.use(
  (response) => {
    // GET-Requests cachen
    if (response.config.method?.toLowerCase() === 'get') {
      const cacheKey = `${response.config.baseURL}${response.config.url}`;
      requestCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401: Versuche Token-Refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Warte auf laufenden Refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        // Kein Refresh Token → Logout
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `http://${window.location.hostname}:3021/api/auth/refresh`,
          { refreshToken }
        );

        const { accessToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        
        processQueue(null, accessToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        
        // Refresh fehlgeschlagen → Logout
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // 429: Rate Limit
    if (error.response?.status === 429) {
      console.error('Rate Limit erreicht. Bitte Seite neu laden.');
    }

    return Promise.reject(error);
  }
);

export default api;
